import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, ShieldCheck, RefreshCw, Calendar, HardDrive, Mail, Clock, Upload, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBackupTab() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [running, setRunning] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSnapshots = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/backup/snapshots`, { headers });
      setSnapshots(r.data?.snapshots || []);
    } catch (e) {
      toast.error('Gagal load snapshots: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSnapshots(); }, []);

  const handleDownloadNow = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/backup/full-export`, {
        headers,
        responseType: 'blob',
      });
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `underads_crm_backup_${Date.now()}.zip`;
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Backup berjaya didownload: ${filename}`);
    } catch (e) {
      toast.error('Download gagal: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setDownloading(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/backup/run-now`, {}, { headers });
      const counts = r.data?.doc_counts || {};
      const total = Object.values(counts).reduce((s, n) => s + n, 0);
      toast.success(`Snapshot baru disimpan: ${total} docs across ${Object.keys(counts).length} collections`);
      await fetchSnapshots();
    } catch (e) {
      toast.error('Snapshot gagal: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const handleDownloadSnapshot = async (snapshotId) => {
    try {
      const r = await axios.get(`${API}/admin/backup/snapshot/${snapshotId}/download`, {
        headers,
        responseType: 'blob',
      });
      const filename = `underads_crm_snapshot_${snapshotId}.zip`;
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Snapshot didownload: ${filename}`);
    } catch (e) {
      toast.error('Download snapshot gagal');
    }
  };

  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restoreAck, setRestoreAck] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleRestore = async () => {
    if (!restoreFile) {
      toast.error('Sila pilih file backup dulu.');
      return;
    }
    if (!restoreAck) {
      toast.error('Sila tick checkbox konfirmasi.');
      return;
    }
    if (restoreConfirm !== 'RESTORE_DATA') {
      toast.error("Sila taip 'RESTORE_DATA' (huruf besar) dalam field konfirmasi.");
      return;
    }
    setRestoring(true);
    setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append('file', restoreFile);
      fd.append('confirm', restoreConfirm);
      fd.append('collections', 'all');
      const r = await axios.post(`${API}/admin/backup/restore`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setRestoreResult(r.data);
      toast.success(`Restore berjaya! Safety snapshot: ${r.data.safety_snapshot_id}`);
      await fetchSnapshots();
      setRestoreFile(null);
      setRestoreConfirm('');
      setRestoreAck(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error('Restore gagal: ' + msg);
      setRestoreResult({ error: msg });
    } finally {
      setRestoring(false);
    }
  };

  const formatBytes = (b) => {
    if (!b) return '0 KB';
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString('en-MY', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="admin-backup-tab">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#10B981]/10 to-[#3B82F6]/5 border border-[#10B981]/20 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="text-[#10B981] flex-shrink-0" size={24} />
          <div>
            <h3 className="text-white text-base font-semibold mb-1">Data Backup System</h3>
            <p className="text-sm text-[#9CA3AF]">
              Backup data automatik untuk elak data loss. <span className="text-[#10B981]">Tier 1 (Daily)</span> simpan snapshot setiap 11:59pm dalam DB (30 hari rolling).{' '}
              <span className="text-[#3B82F6]">Tier 2 (Weekly)</span> hantar email backup ke <code className="text-[#10B981] text-xs">munirrazak@gmail.com</code> setiap Isnin 11:59pm.
            </p>
          </div>
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
              <Download className="text-[#10B981]" size={20} />
            </div>
            <div>
              <h4 className="text-white font-semibold">Download Full Backup Now</h4>
              <p className="text-xs text-[#6B7280]">ZIP (JSON + Excel) — Sales, Marketing, Operation, semua channel</p>
            </div>
          </div>
          <Button
            onClick={handleDownloadNow}
            disabled={downloading}
            data-testid="backup-download-now-btn"
            className="w-full bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white hover:opacity-90"
          >
            {downloading ? <><RefreshCw className="animate-spin mr-2" size={16} />Building backup...</> : <><Download className="mr-2" size={16} />Download ZIP ke PC</>}
          </Button>
        </div>

        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/15 flex items-center justify-center">
              <HardDrive className="text-[#3B82F6]" size={20} />
            </div>
            <div>
              <h4 className="text-white font-semibold">Run Snapshot Now</h4>
              <p className="text-xs text-[#6B7280]">Trigger manual ke DB (collection backups_daily). Berguna sebelum buat operation berisiko.</p>
            </div>
          </div>
          <Button
            onClick={handleRunNow}
            disabled={running}
            data-testid="backup-run-now-btn"
            variant="outline"
            className="w-full border-[#3B82F6]/30 text-[#3B82F6] hover:bg-[#3B82F6]/10"
          >
            {running ? <><RefreshCw className="animate-spin mr-2" size={16} />Creating snapshot...</> : <><Clock className="mr-2" size={16} />Take Snapshot Now</>}
          </Button>
        </div>
      </div>

      {/* Schedule Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="text-[#10B981]" size={16} />
            <span className="text-white text-sm font-semibold">Tier 1 — Daily Auto-Snapshot</span>
          </div>
          <p className="text-xs text-[#6B7280]">Setiap hari 11:59 PM (GMT+8) → snapshot lengkap → simpan dalam DB. Rolling 30 hari (snapshot lama auto-delete).</p>
        </div>
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="text-[#3B82F6]" size={16} />
            <span className="text-white text-sm font-semibold">Tier 2 — Weekly Email Backup</span>
          </div>
          <p className="text-xs text-[#6B7280]">Setiap Isnin 11:59 PM (GMT+8) → email ZIP attachment ke <code className="text-[#10B981]">munirrazak@gmail.com</code>. Backup off-site untuk safety.</p>
        </div>
      </div>

      {/* Restore from Backup File */}
      <div className="bg-gradient-to-br from-[#E63946]/5 to-[#F59E0B]/5 border border-[#E63946]/20 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E63946]/20 flex items-center gap-2">
          <Upload className="text-[#E63946]" size={18} />
          <h4 className="text-white font-semibold text-sm">Restore from Backup File</h4>
          <Badge className="bg-[#E63946]/15 text-[#E63946] text-[10px] ml-auto">DESTRUCTIVE</Badge>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-[#E63946]/5 border border-[#E63946]/20 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="text-[#E63946] flex-shrink-0 mt-0.5" size={14} />
            <p className="text-xs text-[#E63946]/90">
              <span className="font-semibold">Awas:</span> Restore akan <b>REPLACE</b> data semasa dengan data dari fail backup. 
              Sistem akan ambil safety snapshot sebelum restore (boleh undo via download).
              "users" collection <b>tidak akan</b> dipindahkan (untuk elak lockout).
            </p>
          </div>

          <div>
            <label className="text-xs text-[#9CA3AF] mb-1.5 block">Backup file (.zip atau .json)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,.json"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              data-testid="restore-file-input"
              className="block w-full text-sm text-[#9CA3AF] file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#E63946]/15 file:text-[#E63946] hover:file:bg-[#E63946]/25 file:cursor-pointer"
            />
            {restoreFile && (
              <p className="text-xs text-[#10B981] mt-1.5">Pilih: <b>{restoreFile.name}</b> ({(restoreFile.size/1024).toFixed(1)} KB)</p>
            )}
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={restoreAck}
              onChange={(e) => setRestoreAck(e.target.checked)}
              data-testid="restore-ack-checkbox"
              className="mt-0.5 accent-[#E63946]"
            />
            <span className="text-xs text-[#9CA3AF]">
              Saya faham — restore akan replace data semasa dalam DB dengan data dari fail backup ni. 
              Sistem akan ambil safety snapshot sebelum melaksanakannya.
            </span>
          </label>

          <div>
            <label className="text-xs text-[#9CA3AF] mb-1.5 block">
              Taip <code className="text-[#E63946] bg-[#E63946]/10 px-1.5 py-0.5 rounded">RESTORE_DATA</code> untuk konfirmasi (huruf besar):
            </label>
            <input
              type="text"
              value={restoreConfirm}
              onChange={(e) => setRestoreConfirm(e.target.value)}
              placeholder="RESTORE_DATA"
              data-testid="restore-confirm-input"
              className="w-full bg-transparent border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-[#E63946]/50"
            />
          </div>

          <Button
            onClick={handleRestore}
            disabled={restoring || !restoreFile || !restoreAck || restoreConfirm !== 'RESTORE_DATA'}
            data-testid="restore-now-btn"
            className="w-full bg-gradient-to-r from-[#E63946] to-[#F59E0B] text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {restoring ? <><RefreshCw className="animate-spin mr-2" size={16} />Restoring...</> : <><Upload className="mr-2" size={16} />Restore Now</>}
          </Button>

          {restoreResult && !restoreResult.error && (
            <div className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg p-3 text-xs space-y-1">
              <p className="text-[#10B981] font-semibold">✓ Restore berjaya!</p>
              <p className="text-[#9CA3AF]">Backup file dijana: <code>{restoreResult.backup_generated_at}</code></p>
              <p className="text-[#9CA3AF]">Safety snapshot (untuk undo): <code className="text-[#10B981]">{restoreResult.safety_snapshot_id}</code></p>
              <div className="mt-2">
                <p className="text-[#9CA3AF] font-semibold mb-1">Summary:</p>
                <ul className="space-y-0.5">
                  {Object.entries(restoreResult.summary || {}).map(([coll, res]) => (
                    <li key={coll} className="text-[10px] text-[#9CA3AF]">
                      <code>{coll}</code>: {res.restored ? <span className="text-[#10B981]">{res.docs_inserted} docs restored</span> : <span className="text-[#E63946]">{res.reason || res.error || 'skipped'}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Snapshots Table */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[hsl(var(--border))]">
          <div>
            <h4 className="text-white font-semibold text-sm">Snapshot History (Last 30 days)</h4>
            <p className="text-xs text-[#6B7280] mt-0.5">Setiap snapshot boleh didownload bila-bila — restore manual via support.</p>
          </div>
          <Button onClick={fetchSnapshots} size="sm" variant="ghost" className="text-[#6B7280] hover:text-white">
            <RefreshCw size={14} className="mr-1" />Refresh
          </Button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#6B7280] text-sm">Loading...</div>
        ) : snapshots.length === 0 ? (
          <div className="p-8 text-center text-[#6B7280] text-sm">
            Belum ada snapshot lagi. Click "Take Snapshot Now" untuk buat snapshot pertama, atau tunggu auto-snapshot pada 11:59 PM.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--surface-elevated))] text-xs text-[#9CA3AF] uppercase">
                <tr>
                  <th className="text-left px-5 py-2.5">Snapshot ID</th>
                  <th className="text-left px-3 py-2.5">Trigger</th>
                  <th className="text-left px-3 py-2.5">Created (GMT+8)</th>
                  <th className="text-right px-3 py-2.5">Size</th>
                  <th className="text-center px-3 py-2.5">Daily Data</th>
                  <th className="text-center px-3 py-2.5">Channels</th>
                  <th className="text-right px-5 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => (
                  <tr key={s.snapshot_id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-elevated))]/40">
                    <td className="px-5 py-2.5 font-mono text-xs text-white">{s.snapshot_id}</td>
                    <td className="px-3 py-2.5">
                      <Badge className={s.trigger === 'manual' ? 'bg-[#3B82F6]/15 text-[#3B82F6] text-[10px]' : 'bg-[#10B981]/15 text-[#10B981] text-[10px]'}>
                        {s.trigger || 'scheduled'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-[#9CA3AF] text-xs">{formatDate(s.created_at_gmt8 || s.created_at)}</td>
                    <td className="px-3 py-2.5 text-right text-[#9CA3AF] text-xs">{formatBytes(s.size_bytes)}</td>
                    <td className="px-3 py-2.5 text-center text-white text-xs">{s.doc_counts?.daily_data_2026 || 0}</td>
                    <td className="px-3 py-2.5 text-center text-white text-xs">{s.doc_counts?.ibs || 0}</td>
                    <td className="px-5 py-2.5 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadSnapshot(s.snapshot_id)}
                        data-testid={`download-snapshot-${s.snapshot_id}`}
                        className="text-[#10B981] hover:bg-[#10B981]/10 h-7 px-2 text-xs"
                      >
                        <Download size={12} className="mr-1" />ZIP
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
